import DOMPurify from 'dompurify';

/**
 * Regular expression for validating URLs
 * @type {RegExp}
 */
const URL_REGEX = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/[^\s]*)?$/i;

/**
 * List of allowed domains for external resources
 * @type {string[]}
 */
const ALLOWED_DOMAINS = [
  'trusted-domain.com',
  'another-trusted.com',
  // Add more trusted domains as needed
];

/**
 * Configuration for DOMPurify sanitization
 * @type {import('dompurify').Config}
 */
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'a', 'b', 'blockquote', 'br', 'code', 'del', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span', 'strong', 'sub', 'sup', 'table', 'tbody',
    'td', 'th', 'thead', 'tr', 'ul',
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel',
  ],
  ADD_URI_SAFE_ATTR: ['href', 'src'],
  FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'frame', 'frameset', 'meta', 'link'],
  FORBID_ATTR: [
    'on*', 'data-*', 'xmlns', 'formaction', 'form', 'autofocus', 'autocomplete',
    'action', 'onfocus', 'onblur', 'onchange', 'oninput', 'onkeydown', 'onkeypress',
    'onkeyup', 'onmousedown', 'onmousemove', 'onmouseout', 'onmouseover', 'onmouseup',
    'onreset', 'onselect', 'onsubmit', 'onload', 'onerror', 'onabort', 'oncanplay',
    'oncanplaythrough', 'ondurationchange', 'onemptied', 'onended', 'onloadeddata',
    'onloadedmetadata', 'onloadstart', 'onpause', 'onplay', 'onplaying', 'onprogress',
    'onratechange', 'onseeked', 'onseeking', 'onstalled', 'onsuspend', 'ontimeupdate',
    'onvolumechange', 'onwaiting',
  ],
  ALLOW_UNKNOWN_PROTOCOLS: false,
  FORCE_BODY: true,
  SAFE_FOR_TEMPLATES: true,
  WHOLE_DOCUMENT: false,
  SANITIZE_DOM: true,
  KEEP_CONTENT: false,
  IN_PLACE: false,
  RETURN_TRUSTED_TYPE: false,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM_IMPORT: false,
  SANITIZE_NAMED_PROPS: false,
  ALLOW_DATA_ATTR: false,
  USE_PROFILES: {
    html: true,
    svg: false,
    svgFilters: false,
    mathMl: false,
  },
};

/**
 * Cache for sanitized HTML to improve performance
 * @type {Map<string, string>}
 */
const sanitizationCache = new Map();

/**
 * Get current cache size
 * @returns {number} Current number of items in cache
 */
export const getSanitizationCacheSize = () => sanitizationCache.size;

/**
 * Validates if a URL is safe to use
 * @param {string} url - The URL to validate
 * @returns {boolean} - Whether the URL is safe
 */
const isSafeUrl = (url) => {
  try {
    // Handle relative URLs
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
      return true;
    }

    // Handle protocol-relative URLs
    if (url.startsWith('//')) {
      url = `https:${url}`;
    }

    const parsedUrl = new URL(url);

    // Validate URL format
    if (!URL_REGEX.test(url)) {
      return false;
    }

    // Only allow HTTPS URLs for external resources
    if (parsedUrl.protocol !== 'https:' && parsedUrl.hostname !== window.location.hostname) {
      return false;
    }

    // Check against allowed domains
    if (parsedUrl.hostname !== window.location.hostname &&
      !ALLOWED_DOMAINS.some((domain) => parsedUrl.hostname.endsWith(domain))) {
      return false;
    }

    return true;
  } catch (error) {
    console.warn('URL validation failed:', error);
    return false;
  }
};

/**
 * Custom hook to sanitize URLs in attributes
 * @param {string} nodeName - The name of the HTML node
 * @param {string} attrName - The name of the attribute
 * @param {string} attrValue - The value of the attribute
 * @returns {string} - The sanitized attribute value
 */
const sanitizeUrlHook = (nodeName, attrName, attrValue) => {
  if (attrName === 'href' || attrName === 'src') {
    // Decode URL to handle encoded characters
    const decodedUrl = decodeURIComponent(attrValue);

    if (!isSafeUrl(decodedUrl)) {
      return '#';
    }

    // Add noopener and noreferrer to external links
    if (attrName === 'href' && decodedUrl.startsWith('http')) {
      const node = document.createElement('a');
      node.setAttribute('rel', 'noopener noreferrer');
      return decodedUrl;
    }

    return decodedUrl;
  }
  return attrValue;
};

/**
 * Sanitizes HTML input by removing potentially dangerous HTML tags and attributes
 * while preserving safe formatting
 * @param {string} html - The HTML string to sanitize
 * @param {Object} options - Optional configuration
 * @param {number} options.maxCacheSize - Maximum number of items to store in cache
 * @returns {string} - The sanitized HTML string
 */
export const sanitizeHtml = (html, options = {}) => {
  if (!html) return '';

  const maxCacheSize = options.maxCacheSize || 1000;

  // Check cache first
  const cached = sanitizationCache.get(html);
  if (cached) {
    return cached;
  }

  try {
    // Add custom hooks
    DOMPurify.addHook('afterSanitizeAttributes', sanitizeUrlHook);

    // Sanitize the HTML
    const sanitized = DOMPurify.sanitize(html, DOMPURIFY_CONFIG);

    // Remove hooks after sanitization
    DOMPurify.removeHook('afterSanitizeAttributes');

    // Cache the result
    sanitizationCache.set(html, sanitized);

    // Limit cache size to prevent memory issues
    if (sanitizationCache.size > maxCacheSize) {
      const firstKey = sanitizationCache.keys().next().value;
      sanitizationCache.delete(firstKey);
    }

    return sanitized;
  } catch (error) {
    console.error('HTML sanitization failed:', error);
    return ''; // Return empty string on error
  }
};
